import React, { useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSessionContext } from 'kmstack-shared/hooks';
import { PlanType, RoleType } from 'kmstack-shared/core';
import { getEnvConfig, getBillingNotice } from 'kmstack-shared/utils';
import { DashboardView, DashboardViewTabs, NavLink } from '../types';
import { hasAnyFeature } from 'kmstack-shared/apps/accounting';
import {
  AppLogoIcon,
  MenuDoubleArrowLeftIcon,
  UserCircleIcon
} from '../assets/icons';
import {
  dashBoardEssentialsLinks,
  dashBoardToolsLinks,
  employeePortalLinks,
  featureFlagsForDashboardView,
  featureFlagsForLink,
  findCurrentLink,
  payrollSidebarLinks,
  workforceSidebarLinks
} from '../common/utils/tabs';


interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  currentDashboardView: DashboardView;
  setCurrentDashboardView: (view: DashboardView) => void;
  disabled?: boolean;
}


interface SidebarSectionProps {
  title: string;
  links: NavLink[];
  isOpen: boolean;
  currentDashboardView: DashboardView;
  setCurrentDashboardView: (view: DashboardView) => void;
  pathname: string;
  commonLinkClasses: string;
  activeLinkClasses: string;
  labelClasses: string;
  disabled?: boolean;
}
const SidebarSection: React.FC<SidebarSectionProps> = React.memo(({
  title,
  links,
  isOpen,
  currentDashboardView,
  setCurrentDashboardView,
  pathname,
  commonLinkClasses,
  activeLinkClasses,
  labelClasses,
  disabled
}) => {
  const isLinkActive = (link: NavLink) => {
    const href = link.href.startsWith('/') ? link.href : `/${ link.href }`;
    return pathname === href || pathname.startsWith(`${ href }/`) || (!!link.viewId && currentDashboardView === link.viewId);
  };

  return (
    <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>
      {isOpen && (
        <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {title}
        </h3>
      )}
      <ul className="space-y-1">
        {links.map(link => {
          const active = isLinkActive(link);

          return (
            <li key={`${ title }-${ link.name }`}>
              <Link
                to={`/${ link.href }`}
                className={`${ commonLinkClasses } ${ active ? activeLinkClasses : 'text-gray-700' }`}
                title={!isOpen ? link.name : ''}
                aria-current={active ? 'page' : undefined}
                onClick={() => {
                  const nextView = link.parentViewId || link.viewId;
                  if (nextView) setCurrentDashboardView(nextView as DashboardView);
                }}
                tabIndex={disabled ? -1 : 0}
              >
                {link.icon}
                <span className={labelClasses}>{link.name}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
});

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  toggleSidebar,
  currentDashboardView,
  setCurrentDashboardView,
  disabled
}) => {
  const { sessionContext } = useSessionContext();
  // const membership = getUserMembershipForActiveOrg(sessionContext);
  const userRole = sessionContext?.activeMembership?.role?.name;
  const isEmployeePortalUser = userRole === RoleType.ACCOUNT_EMPLOYEE;
  const currentPlan = sessionContext?.activeMembership?.planType || PlanType.FREE;
  const isFreePlan = currentPlan === PlanType.FREE;
  const billingNotice = getBillingNotice(sessionContext?.activeMembership);
  const billingRestricted = Boolean(billingNotice);
  const accountUrl = getEnvConfig().accountUrl.replace(/\/$/, '');
  const billingUrl = `${ accountUrl }/account/billing`;

  const userName = sessionContext?.user?.name || '';
  const userEmail = sessionContext?.user?.email || '';
  // const businessName = membership?.org?.legalName ?? membership?.org?.name ?? '';
  const commonLinkClasses = "flex items-center px-4 py-2.5 rounded-lg hover:bg-primaryGreen/10 hover:text-primaryGreen transition-colors duration-200";
  const activeLinkClasses = "bg-primaryGreen/10 text-primaryGreen font-semibold";
  const iconClasses = `w-5 h-5 mr-3 ${ isOpen ? '' : 'mx-auto' }`; // Center icon when closed
  const labelClasses = `${ isOpen ? 'opacity-100' : 'opacity-0 lg:hidden' } transition-opacity duration-200 whitespace-nowrap`;
  const canShowLink = useCallback((link: NavLink) => {
    if (link.visibleOnPlans && !link.visibleOnPlans.includes(currentPlan)) return false;

    const features = featureFlagsForLink(link);
    return features.length === 0 || hasAnyFeature(currentPlan, features, userRole);
  }, [currentPlan, userRole]);

  const essentialsLinks: NavLink[] = useMemo(
    () => {
      const links = isEmployeePortalUser
        ? employeePortalLinks(iconClasses)
        : dashBoardEssentialsLinks(iconClasses);

      return links.filter(link => {
        const features = featureFlagsForDashboardView(link.viewId);
        return features.length === 0 || hasAnyFeature(currentPlan, features, userRole);
      });
    },
    [iconClasses, isEmployeePortalUser, currentPlan, userRole]
  );
  const toolsLinks: NavLink[] = useMemo(
    () => {
      if (isEmployeePortalUser) return [];

      return dashBoardToolsLinks(iconClasses).filter(link => {
        const features = featureFlagsForDashboardView(link.viewId);
        return features.length === 0 || hasAnyFeature(currentPlan, features, userRole);
      });
    },
    [iconClasses, isEmployeePortalUser, currentPlan, userRole]
  );
  const workforceLinks: NavLink[] = useMemo(
    () => {
      if (isEmployeePortalUser) return [];

      return workforceSidebarLinks(iconClasses).filter(canShowLink);
    },
    [iconClasses, isEmployeePortalUser, canShowLink]
  );
  const payrollLinks: NavLink[] = useMemo(
    () => {
      if (isEmployeePortalUser) return [];

      return payrollSidebarLinks(iconClasses).filter(canShowLink);
    },
    [iconClasses, isEmployeePortalUser, canShowLink]
  );
  const visibleLinks = useMemo(
    () => [...essentialsLinks, ...workforceLinks, ...payrollLinks, ...toolsLinks],
    [essentialsLinks, workforceLinks, payrollLinks, toolsLinks]
  );

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, viewId?: DashboardView) => {
    e.preventDefault();
    if (viewId) {
      setCurrentDashboardView(viewId);
    }
  };

  // Determine current link from URL using utility
  const pathname = window.location.pathname.split('?')[0].split('#')[0];
  const currentLink = useMemo(() => findCurrentLink(pathname), [pathname]);

  useEffect(() => {
    const currentViewId = currentLink?.parentViewId || currentLink?.viewId;
    const canViewLink = visibleLinks.some(link => {
      const linkViewId = link.parentViewId || link.viewId;
      return linkViewId === currentViewId;
    });

    if (currentViewId && canViewLink && currentViewId !== currentDashboardView) {
      setCurrentDashboardView(currentViewId as DashboardView);
    }
  }, [currentLink, currentDashboardView, setCurrentDashboardView, visibleLinks]);

  useEffect(() => {
    const canViewCurrent = visibleLinks.some(link => {
      const linkViewId = link.parentViewId || link.viewId;
      return linkViewId === currentDashboardView;
    });

    if (!canViewCurrent) {
      const fallbackView = visibleLinks[0]?.parentViewId || visibleLinks[0]?.viewId || DashboardViewTabs.OVERVIEW;
      setCurrentDashboardView(fallbackView as DashboardView);
    }
  }, [currentDashboardView, visibleLinks, setCurrentDashboardView]);

  return (
    <aside
      className={`fixed top-0 left-0 z-40 h-screen w-64 bg-white shadow-lg flex flex-col transition-all duration-300 ease-in-out text-darkGreen
                  ${ isOpen ? 'translate-x-0 lg:w-64' : '-translate-x-full lg:translate-x-0 lg:w-20' } ${ disabled ? 'pointer-events-none' : '' }`}
    >
      {/* Header */}
      <div className={`flex items-center justify-between h-16 border-b border-gray-200 px-4 ${ isOpen ? '' : 'px-0 justify-center' } ${ disabled ? 'opacity-50' : '' }`}>
        {isOpen && (
          <a href="/" onClick={(e) => handleLinkClick(e, DashboardViewTabs.OVERVIEW)} className="flex items-center text-xl font-bold text-primaryGreen" tabIndex={disabled ? -1 : 0}>
            <AppLogoIcon className="w-8 h-8 mr-2" />
            {/* we will go with kmstack instead of business and we will find an alternative place to place business name */}
            Kmstack
            {/* {businessName} */}
          </a>
        )}
        <button onClick={toggleSidebar} className={`p-2 rounded-md hover:bg-gray-100 ${ isOpen ? '' : 'w-full flex justify-center py-4' }`} aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"} disabled={disabled}>
          <MenuDoubleArrowLeftIcon className={`w-6 h-6 text-gray-600 transition-transform duration-300 ${ isOpen ? '' : 'rotate-180' }`} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-grow px-2 py-4 space-y-4 overflow-y-auto">
        <SidebarSection
          title="Essentials"
          links={essentialsLinks}
          isOpen={isOpen}
          currentDashboardView={currentDashboardView}
          setCurrentDashboardView={setCurrentDashboardView}
          pathname={pathname}
          commonLinkClasses={commonLinkClasses}
          activeLinkClasses={activeLinkClasses}
          labelClasses={labelClasses}
          disabled={disabled}
        />
        {workforceLinks.length > 0 && (
          <SidebarSection
            title="Workforce"
            links={workforceLinks}
            isOpen={isOpen}
            currentDashboardView={currentDashboardView}
            setCurrentDashboardView={setCurrentDashboardView}
            pathname={pathname}
            commonLinkClasses={commonLinkClasses}
            activeLinkClasses={activeLinkClasses}
            labelClasses={labelClasses}
            disabled={disabled}
          />
        )}
        {payrollLinks.length > 0 && (
          <SidebarSection
            title="Payroll"
            links={payrollLinks}
            isOpen={isOpen}
            currentDashboardView={currentDashboardView}
            setCurrentDashboardView={setCurrentDashboardView}
            pathname={pathname}
            commonLinkClasses={commonLinkClasses}
            activeLinkClasses={activeLinkClasses}
            labelClasses={labelClasses}
            disabled={disabled}
          />
        )}
        {toolsLinks.length > 0 && (
          <SidebarSection
            title="Tools & Settings"
            links={toolsLinks}
            isOpen={isOpen}
            currentDashboardView={currentDashboardView}
            setCurrentDashboardView={setCurrentDashboardView}
            pathname={pathname}
            commonLinkClasses={commonLinkClasses}
            activeLinkClasses={activeLinkClasses}
            labelClasses={labelClasses}
            disabled={disabled}
          />
        )}
      </nav>


      {/* Plan CTA */}
      {!isEmployeePortalUser && (
        <div className={`px-4 py-3 border-t border-gray-200 ${ isOpen ? 'block' : 'hidden' }`}>
          <div className="p-3 bg-darkGreen text-white rounded-lg">
            <h4 className="font-semibold text-sm mb-1">
              {billingRestricted ? 'Free plan' : isFreePlan ? 'Free plan' : `${ currentPlan } plan`}
            </h4>
            {billingRestricted && (
              <p className="text-xs leading-relaxed mb-2">
                {billingNotice} Free features remain available. Restore your plan to access paid features.
              </p>
            )}
            <a
              href={billingUrl}
              className="block w-full mt-1 px-3 py-1.5 text-center text-xs font-medium text-darkGreen bg-primaryGreen rounded-md hover:bg-opacity-90 transition-colors"
            >
              {billingRestricted ? 'Resolve billing' : isFreePlan ? 'Upgrade plan' : 'Manage billing'}
            </a>
          </div>
        </div>
      )}

      {/* User Profile */}
      <div className={`p-4 border-t border-gray-200 ${ isOpen ? 'flex items-center' : 'flex flex-col items-center py-3' }`}>
        <UserCircleIcon className={`w-10 h-10 text-gray-400 ${ isOpen ? 'mr-3' : 'mb-1' }`} />
        <div className={`${ isOpen ? 'block' : 'hidden' } text-left`}>
          <p className="text-sm font-semibold text-darkGreen whitespace-nowrap">{userName}</p>
          <p className="text-xs text-gray-500 whitespace-nowrap">{userEmail}</p>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
